import { ComponentProps, useCallback } from 'react';

import { endOfDay, setHours, setMilliseconds, setMinutes, setSeconds } from 'date-fns';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../../Button';
import { Switch } from '../../../Fields';
import { Popover } from '../../../Popover';

import { List } from './List';
import { TimeSelection } from './TimeSelection';
import { isEndOfDay } from './utils';

type Hour = Exclude<ComponentProps<typeof TimeSelection>['hour'], undefined>;

interface TimeBoxSingleProps {
  readonly rangePicker?: false;
  readonly endDate?: never;
  readonly onChangeDate?: (startDate: Date) => void;
}

interface TimeBoxRangeProps {
  readonly rangePicker: true;
  readonly endDate: Date;
  readonly onChangeDate?: (startDate: Date, endDate: Date) => void;
}

interface TimeBoxCommonProps {
  readonly startDate: Date;
  readonly timePicker?: boolean;
  readonly allDay?: boolean;
  readonly onToggleAllDay?: (allDay: boolean) => void;
  readonly onApply: () => void;
  readonly className?: string;
}

type TimeBoxProps = TimeBoxCommonProps & (TimeBoxSingleProps | TimeBoxRangeProps);

function TimeBox({ startDate, endDate, className, allDay, timePicker, rangePicker, onToggleAllDay, onApply, onChangeDate }: TimeBoxProps) {
  const classes = twMerge('flex flex-col p-6', className);

  const onChangeStartingHour = useCallback(
    (hour: Hour) => {
      let start = startDate;

      if (hour === '24') {
        start = endOfDay(start);
      } else {
        start = setMilliseconds(setSeconds(setMinutes(setHours(start, Number(hour)), 0), 0), 0);
      }

      if (rangePicker) {
        onChangeDate?.(start, endDate);
      } else {
        onChangeDate?.(start);
      }
    },
    [startDate, endDate, rangePicker, onChangeDate],
  );

  const onChangeEndingHour = useCallback(
    (hour: Hour) => {
      let end = endDate as Date;

      if (hour === '24') {
        end = endOfDay(end);
      } else {
        end = setMilliseconds(setSeconds(setMinutes(setHours(end, Number(hour)), 0), 0), 0);
      }

      onChangeDate?.(startDate, end);
    },
    [startDate, endDate, onChangeDate],
  );

  const onChangeStartDate = useCallback(
    (date: Date) => {
      if (rangePicker) {
        onChangeDate?.(date, endDate);
      } else {
        onChangeDate?.(date);
      }
    },
    [onChangeDate, endDate, rangePicker],
  );

  const onChangeEndDate = useCallback(
    (date: Date) => {
      onChangeDate?.(startDate, date);
    },
    [onChangeDate, startDate],
  );

  return (
    <div className={classes}>
      {timePicker ? (
        <div className="mb-5 flex flex-row items-start">
          <label className="flex w-full items-center text-base font-normal">
            All day
            <span className="flex flex-auto" />
            <Switch size="lg" checked={allDay} onCheckedChange={onToggleAllDay} />
          </label>
        </div>
      ) : null}
      {rangePicker ? (
        <>
          <List header="Starts" date={startDate} onChangeDate={onChangeStartDate} className="mb-3">
            {timePicker ? (
              <TimeSelection
                exclude24
                name="start-at"
                disabled={allDay}
                hour={String(startDate.getHours()).padStart(2, '0') as Hour}
                onChangeHour={onChangeStartingHour}
              />
            ) : null}
          </List>
          <List header="Ends" date={endDate} onChangeDate={onChangeEndDate}>
            {timePicker ? (
              <TimeSelection
                name="end-at"
                disabled={allDay}
                isEnd={isEndOfDay(endDate)}
                hour={String(endDate.getHours()).padStart(2, '0') as Hour}
                onChangeHour={onChangeEndingHour}
              />
            ) : null}
          </List>
        </>
      ) : (
        <List date={startDate} onChangeDate={onChangeStartDate} className="mb-3">
          {timePicker ? (
            <TimeSelection
              disabled={allDay}
              isEnd={isEndOfDay(startDate)}
              hour={String(startDate.getHours()).padStart(2, '0') as Hour}
              onChangeHour={onChangeStartingHour}
            />
          ) : null}
        </List>
      )}
      <div className="flex flex-auto" />
      <Popover.Close asChild>
        <Button variant="primary" className="mb-0.5" onClick={onApply}>
          Apply
        </Button>
      </Popover.Close>
    </div>
  );
}

export { TimeBox };
